import { RootStore } from './root.store';
import { makeAutoObservable, runInAction } from 'mobx';
import { IAddress, IContact } from '../domains/common.domain';
import { IShippingInformation, SHIPPING_OPTION_DEFAULT, SHIPPING_OPTION_UNAVAILABLE } from '../domains/shipping-information.domain';
import { IShippingMethod, IShippingOption } from '../domains/signup.domain';
import { recalculateShipping } from '../api/signup';
import { getFormattedPrice } from '../utils/formatter';

export class ShippingInformationStore {
  root: RootStore;
  private _isSameAsFirstOnboardingContact: boolean = false;
  private _isSameAsServiceAddress: boolean = false;
  private _information: IShippingInformation = {
    shippingMethod: { value: SHIPPING_OPTION_UNAVAILABLE, errorType: 'required' },
    timeRestriction: { value: '', errorType: undefined },
  };
  private _shippingOptions: IShippingOption[] = [];
  private _isShippingCalculated: boolean = true;
  private _isRecalculating: boolean = false;
  private _isNewShippingInfo: boolean = false;

  constructor(root: RootStore) {
    makeAutoObservable(this);
    this.root = root;
  }

  init() {
    this.root.shippingAddressStore.init('shipping');
    this.root.shippingContactStore.init('shipping');

    if (this.root.tenantStore.config.allowShipmentOptions) {
      const quoteInfo = this.root.quoteStore.quoteInfo;
      let shippingMethod = SHIPPING_OPTION_UNAVAILABLE;
      let price = 0;
      if (quoteInfo?.shipping?.shippingOption && quoteInfo.shipping?.cost > 0) {
        shippingMethod = quoteInfo?.shipping?.shippingOption;
        price = quoteInfo.shipping?.cost / 100;
      }
      this.updateShippingMethods([{ method: shippingMethod, price }], false);
      this.root.shippingInformationStore.updateInformation('shippingMethod', shippingMethod);
    }
  }

  get isFormValid(): boolean {
    this.validateShippingMethod();
    return (
      this.root.shippingAddressStore.isAddressValid &&
      this.root.shippingContactStore.isFormValid &&
      !this.information.shippingMethod.errorType
    );
  }

  private validateShippingMethod() {
    this._information.shippingMethod.errorType =
      !this.root.tenantStore.config.allowShipmentOptions || (this.isShippingCalculated && !this.isRecalculating) ? undefined : 'invalid';
  }

  async updateAddress(field: keyof IAddress, value: string) {
    this._isShippingCalculated = false;
    this._information.shippingMethod.errorType = this._information.shippingMethod.warningType = undefined;
    this.root.billingInformationStore.resetAddressType();
    this.root.shippingAddressStore.updateAddress(field, value);
    if (field === 'autocompleteSelectedAddress') {
      await this.recalculateShipping();
    }
  }

  async updateSameAsServiceAddress() {
    this._isSameAsServiceAddress = !this._isSameAsServiceAddress;
    if (this._isSameAsServiceAddress) {
      this.root.shippingAddressStore.changeAddress({
        ...this.root.serviceAddressStore.address,
        recipient: this.root.shippingAddressStore.address.recipient,
      });
      await this.recalculateShipping();
    } else {
      this.root.shippingAddressStore.resetAddress();
    }
  }

  get isSameAsServiceAddress(): boolean {
    return this._isSameAsServiceAddress;
  }

  uncheckSameAsServiceAddress() {
    this._isSameAsServiceAddress = false;
  }

  updateContact(field: keyof IContact, value: string) {
    this._isSameAsFirstOnboardingContact = false;
    this.root.shippingContactStore.updateContact(0, field, value);
  }

  uncheckSameAsFirstOnboardingContact() {
    this._isSameAsFirstOnboardingContact = false;
  }

  updateSameAsFirstOnboardingContact() {
    this._isSameAsFirstOnboardingContact = !this._isSameAsFirstOnboardingContact;
    if (this._isSameAsFirstOnboardingContact) {
      this.root.shippingContactStore.contacts = [{ ...this.root.onboardingContactsStore.contacts[0] }];
    } else {
      this.root.shippingContactStore.resetContacts();
    }
  }

  get isSameAsFirstOnboardingContact(): boolean {
    return this._isSameAsFirstOnboardingContact;
  }

  get information(): IShippingInformation {
    return this._information;
  }

  updateInformation(field: keyof IShippingInformation, value: string) {
    if (field === 'shippingMethod') {
      this._information[field] = {
        value,
        errorType: !value || !this.isShippingCalculated ? 'invalid' : undefined,
        warningType: value === SHIPPING_OPTION_UNAVAILABLE ? 'unavailable' : undefined,
      };
    }
    if (field === 'timeRestriction') {
      this._information[field] = {
        value: value,
        errorType: undefined,
      };
    }
  }

  get formattedShippingInfo(): string {
    const { culture, currency } = this.root.tenantStore.config;
    let info = SHIPPING_OPTION_UNAVAILABLE;
    const price = this.selectedShippingMethod?.price ?? 0;
    if (price > 0) {
      const shippingOption = this.selectedShippingMethod?.method ?? SHIPPING_OPTION_DEFAULT;
      info = `${getFormattedPrice(price, culture, currency, false)} / ${shippingOption}`;
    }
    return info;
  }

  get selectedShippingMethod(): IShippingMethod {
    const selectedOption = this._shippingOptions.find(x => x.value === this._information?.shippingMethod?.value);
    return selectedOption
      ? { method: selectedOption.value, price: selectedOption.price }
      : { method: SHIPPING_OPTION_UNAVAILABLE, price: 0 };
  }

  get shippingOptions(): IShippingOption[] {
    return this._shippingOptions;
  }

  updateShippingMethods(methods: IShippingMethod[] = [], isNewInfo: boolean = true) {
    runInAction(() => {
      let method = SHIPPING_OPTION_UNAVAILABLE;
      let options: IShippingOption[];
      if (methods?.length > 0) {
        const { culture, currency } = this.root.tenantStore.config;
        options = methods.map(x => ({
          value: x.method,
          name: `${x.method} ~ ${getFormattedPrice(x.price, culture, currency, false)}`,
          price: x.price,
        }));
        if (this._information.shippingMethod.value && options.some(x => x.value === this._information.shippingMethod.value)) {
          method = this._information.shippingMethod.value;
        } else {
          method = options.some(x => x.value === SHIPPING_OPTION_DEFAULT) ? SHIPPING_OPTION_DEFAULT : options[0].value;
        }
      } else {
        options = [
          {
            value: SHIPPING_OPTION_UNAVAILABLE,
            name: SHIPPING_OPTION_UNAVAILABLE,
            price: 0,
          },
        ];
      }

      this._shippingOptions = options;
      this._isShippingCalculated = true;
      this._isRecalculating = false;
      this.updateInformation('shippingMethod', method);
      this._isNewShippingInfo = isNewInfo;
    });
  }

  async recalculateShipping() {
    if (!this.root.tenantStore.config.allowShipmentOptions) return;

    if (this.isRecalculating) return;
    if (!this.root.shippingAddressStore.isAddressValid) {
      this.updateShippingMethods();
      return;
    }

    try {
      this._isShippingCalculated = false;
      this._isRecalculating = true;

      const address = this.root.shippingAddressStore.address;
      const methods = await recalculateShipping({
        address1: address.address?.value,
        address2: address.secondaryAddress?.value,
        city: address.city?.value,
        state: address.state?.value,
        zip: address.zipCode?.value,
        country: address.country?.value,
        devices: this.root.quoteStore.shippableItems,
      });
      this.updateShippingMethods(methods);
    } catch {
      this.updateShippingMethods();
    }
  }

  get isShippingCalculated() {
    return this._isShippingCalculated;
  }

  get isRecalculating() {
    return this._isRecalculating;
  }

  get isNewShippingInfo() {
    return this._isNewShippingInfo;
  }
}
